import { buildColumns, CommandBar, DefaultButton, DetailsList, DetailsListLayoutMode, IColumn, IconButton, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, SelectionMode, Stack, TextField, Toggle } from '@fluentui/react';
import * as React from 'react';
import { APIPaths, deleteItem, getItems, ICRUDProps, IItem, updateItem } from '../../data/sql';
import { ConfirmDialog } from '../simpleDialog';
import MiniColorPicker from './colorPicker';

const CRUDPanel: React.FunctionComponent<{ item: IItem, idField: string, apiPaths: APIPaths, onClose: () => void, friendlyName: string, columns: IColumn[] }> = ({ item, idField, apiPaths, onClose, friendlyName, columns }) => {

    const [updatedItem, setUpdatedItem] = React.useState<IItem>({ ...item });
    const [error, setError] = React.useState<any>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [isSaving, setIsSaving] = React.useState(false);

    return <Panel
        isOpen={true}
        type={PanelType.medium}
        onDismiss={onClose}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack tokens={{ childrenGap: 6 }}>
                {error && <MessageBar messageBarType={MessageBarType.error}>{error.message || error}</MessageBar>}
                {successMessage && <MessageBar messageBarType={MessageBarType.success}>{successMessage}</MessageBar>}
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!successMessage && <PrimaryButton text='Submit' disabled={JSON.stringify(updatedItem) === JSON.stringify(item) || isSaving} onClick={() => {
                        setIsSaving(true)
                        setError(undefined)
                        updateItem(apiPaths.addOrUpdateItem, updatedItem).then(res => {
                            if (res.Prefix === "RequestError") {
                                setError(JSON.stringify(res));
                            }
                            else {
                                setSuccessMessage(`Successfully ${updatedItem[idField] === -1 ? "created" : "updated"} ${friendlyName}`);
                            }                            
                        }).catch((err: any) => {
                            setError(err)
                        }).finally(() => {
                            setIsSaving(false)
                        });
                    }} />}
                    <DefaultButton text={successMessage ? "Close" : "Cancel"} onClick={onClose} />
                </Stack></Stack>
        }}
        headerText={item[idField] === -1 ? `New ${friendlyName}` : `Modify ${friendlyName}`}>
        <Stack>
            {columns.map(column => {
                let retVal = <>Unknown field type</>;
                switch (column.data?.type) {
                    case "Toggle":
                        retVal = <Toggle
                            key={`Toggle-field-${column.key}`}
                            label={column.name}
                            onChange={(_ev, checked) => {
                                const newItem = { ...updatedItem };
                                (newItem as any)[column.key] = checked || false;
                                setUpdatedItem(newItem);
                            }} checked={updatedItem[column.key] || undefined} />
                        break;
                    case "ColorPicker":
                        retVal =  <MiniColorPicker label='Color' value={updatedItem[column.key]} onChange={(newValue) => {
                            const newItem = { ...updatedItem };
                            (newItem as any)[column.key] = newValue || "";
                            setUpdatedItem(newItem);
                        }} />
                        break;
                    default:
                        retVal = <TextField
                            key={`Text-field-${column.key}`}
                            label={column.name}
                            value={(updatedItem as any)[column.key]?.toString()}
                            onChange={(_ev, newValue) => {
                                const newItem = { ...updatedItem };
                                (newItem as any)[column.key] = newValue || "";
                                setUpdatedItem(newItem);
                            }} />
                }
                return retVal;
            })}
        </Stack>
    </Panel>
}


const CRUDTable: React.FunctionComponent<{ props: ICRUDProps }> = ({ props }) => {

    const [items, setItems] = React.useState<IItem[]>([]);
    const [itemToEdit, setItemToEdit] = React.useState<IItem>();
    const [itemToDelete, setItemToDelete] = React.useState<IItem>();
    const [refreshFlag, setRefreshFlag] = React.useState(false);
    const [error, setError] = React.useState<any>();
    const [deletingError, setDeletingError] = React.useState<any>();

    React.useEffect(() => {
        getItems(props.apiPaths.getItems).then(prefixes => {
            setItems(prefixes);
        }).catch(err => {
            setError(err);
        })
    }, [refreshFlag, props.apiPaths])

    let cols: IColumn[] = [];
    if(props.buildColumns)
    {
        cols = buildColumns(items) || [];
        props.columnsToFilter?.forEach(column => {
            cols = cols.filter(c => c.key !== column);
        });        
    }
    cols = (cols || []).concat(props.columns)
    cols = cols.concat(props.additionalColumns || []);

    let _columns: IColumn[] = [
        {
            key: "Edit",
            name: "Edit",
            minWidth: 40,
            onRender: (item) => {
                return <IconButton iconProps={{ iconName: "Edit" }} onClick={() => {
                    setItemToEdit(item);
                }} />
            }
        },
        ...cols,
        {
            key: "Delete",
            name: "Delete",
            minWidth: 50,
            onRender: (item) => {
                return <IconButton iconProps={{ iconName: "Delete" }} onClick={() => {
                    setItemToDelete(item);
                }} />
            }
        }
    ]

    const _deleteItem = () => {
        if (itemToDelete) {
            deleteItem(props.apiPaths.deleteItem, itemToDelete).then(res => {
                setItemToDelete(undefined);
                setRefreshFlag(!refreshFlag);
            }).catch(err => {
                setDeletingError(err);
            })
        }
    }


    return <Stack>
        <h2>{props.friendlyName} Settings</h2>
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        <CommandBar items={[{
            key: "Add", text: "Add", onClick: () => {
                const itemToEdit = {};
                (itemToEdit as any)[props.idField] = -1;
                setItemToEdit(itemToEdit);
            }, iconProps: { iconName: "Add" }
        }]} />
        <DetailsList items={items}
            columns={_columns}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            selectionMode={SelectionMode.none}
        />
        {itemToDelete && <ConfirmDialog
            message={deletingError || `Are you sure you want to delete the ${props.friendlyName}?`}
            onClose={(isConfirmed) => isConfirmed ? _deleteItem() : setItemToDelete(undefined)}
            title={`Delete ${props.friendlyName}`}

        />}
        {itemToEdit && <CRUDPanel item={itemToEdit} onClose={() => {
            setItemToEdit(undefined);
            setRefreshFlag(!refreshFlag);
        }} idField={props.idField}
            apiPaths={props.apiPaths}
            friendlyName={props.friendlyName}
            columns={cols} />}
    </Stack>;

};

export default CRUDTable;